/**
 * Files tab on entity detail pages. Drag-and-drop dropzone + list of
 * `file.object` entities already attached to the entity via graph
 * links. Drop a file → POST /files/upload → `file.attach` to bind it
 * to the host entity → optimistic append to the local list so the new
 * row shows without a round-trip to the parent loader.
 */

import { useCallback, useState } from "react";
import type { DragEvent, JSX } from "react";

import { Icon, Stack, Text } from "../../components/ui";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { uploadBrowserFile, type UploadedFile } from "../../hooks/fileUpload";
import { ExpandableEntityCard } from "../../components/agent/ExpandableEntityCard";
import type { LinkedEntitySummary } from "./sharedTypes";

export interface FilesTabProps {
  readonly entityId: string;
  readonly attachedFiles: readonly LinkedEntitySummary[];
}

interface LocalFile {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
}

export function FilesTab({ entityId, attachedFiles }: FilesTabProps): JSX.Element {
  const runtime = useAppRuntime();
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Optimistic local list. The `attachedFiles` prop comes from the
  // parent loader (contacts.get / companies.get / projects.get) and
  // will catch up on next navigation; until then we append locally.
  const [justUploaded, setJustUploaded] = useState<LocalFile[]>([]);

  const handleUpload = useCallback(
    async (file: File): Promise<void> => {
      setError(null);
      setUploading(true);
      try {
        const uploaded: UploadedFile = await uploadBrowserFile(
          runtime.transport,
          file,
        );
        await runtime.transport.rpc("file.attach", {
          file_id: uploaded.id,
          target_id: entityId,
        });
        setJustUploaded((prev) => [
          ...prev,
          {
            id: uploaded.id,
            name: uploaded.name,
            mimeType: uploaded.mimeType,
            sizeBytes: uploaded.sizeBytes,
          },
        ]);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setUploading(false);
      }
    },
    [runtime, entityId],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      // Sequential to avoid swamping the upload endpoint.
      void (async (): Promise<void> => {
        for (const f of files) {
          await handleUpload(f);
        }
      })();
    },
    [handleUpload],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((): void => {
    setDragOver(false);
  }, []);

  const handlePickClick = useCallback((): void => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.style.display = "none";
    input.addEventListener("change", () => {
      const list = Array.from(input.files ?? []);
      input.remove();
      void (async (): Promise<void> => {
        for (const f of list) {
          await handleUpload(f);
        }
      })();
    });
    document.body.appendChild(input);
    input.click();
  }, [handleUpload]);

  // Merge prop files (already-persisted) with optimistic local appends.
  // Local entries that the prop later includes get deduped by id.
  const propIds = new Set(attachedFiles.map((f) => f.id));
  const optimistic = justUploaded.filter((f) => !propIds.has(f.id));

  return (
    <Stack gap={4} className="px-5 py-4">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handlePickClick}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed py-10 cursor-pointer transition-colors ${
          dragOver
            ? "border-accent bg-accent/5"
            : "border-edge bg-surface-secondary/40 hover:bg-surface-secondary/60"
        }`}
      >
        <Icon name="paperclip" size={20} className="text-content-tertiary" />
        <Text variant="body" color="secondary" className="text-sm">
          {uploading ? "Uploading…" : "Drop files here, or click to browse"}
        </Text>
        {error !== null && (
          <Text variant="body" color="tertiary" className="text-xs text-error">
            {error}
          </Text>
        )}
      </div>

      {(attachedFiles.length > 0 || optimistic.length > 0) && (
        <Stack gap={2}>
          {attachedFiles.map((e) => (
            <ExpandableEntityCard
              key={e.id}
              schemaId={e.schema_id}
              data={{ id: e.id, name: e.name, ...e.data }}
              runtime={runtime}
            />
          ))}
          {optimistic.map((f) => (
            <ExpandableEntityCard
              key={f.id}
              schemaId="file.object"
              data={{
                id: f.id,
                name: f.name,
                mime_type: f.mimeType,
                size_bytes: f.sizeBytes,
              }}
              runtime={runtime}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
